import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { SUBJECTS } from '@/data/flashcards';
import { User, LogOut, Trophy, BookOpen, Target, Flame, Calendar, TrendingUp, FileText, ChevronRight } from 'lucide-react';
import LegalScreen from '@/components/legal/LegalScreen';

const ProfileScreen = () => {
  const { user, logout, getSubjectProgress } = useAuth();
  const [showLegal, setShowLegal] = useState(false);

  // If legal screen is shown, render it
  if (showLegal) {
    return <LegalScreen onBack={() => setShowLegal(false)} />;
  }

  const totalCards = SUBJECTS.length * 50;
  const totalCorrect = SUBJECTS.reduce((acc, subject) => {
    return acc + getSubjectProgress(subject.id).correct;
  }, 0);
  const totalStudied = SUBJECTS.reduce((acc, subject) => {
    return acc + getSubjectProgress(subject.id).studied;
  }, 0);

  const overallProgress = Math.round((totalCorrect / totalCards) * 100);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <h2 className="text-2xl font-display font-bold text-foreground">Profil</h2>
        <p className="text-muted-foreground text-sm mt-1">Dein Lernfortschritt</p>
      </div>

      {/* User Info Card */}
      <div className="px-4 mb-6">
        <Card className="bg-gradient-card border-border overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-foreground">
                  {user?.name || 'Lernender'}
                </h3>
                <p className="text-sm text-muted-foreground">{user?.phone}</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                <Flame className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{user?.stats?.streak || 0}</p>
                <p className="text-xs text-muted-foreground">Tage Streak</p>
              </div>
              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                <BookOpen className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{totalStudied}</p>
                <p className="text-xs text-muted-foreground">Gelernt</p>
              </div>
              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                <Target className="w-5 h-5 text-success mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{totalCorrect}</p>
                <p className="text-xs text-muted-foreground">Gemeistert</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overall Progress */}
      <div className="px-4 mb-6">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">Gesamtfortschritt</span>
              </div>
              <span className="text-lg font-bold text-primary">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-3 bg-muted" />
            <p className="text-xs text-muted-foreground mt-2">
              {totalCorrect} von {totalCards} Karten gemeistert
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Subject Progress */}
      <div className="px-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Fortschritt nach Fach</h3>
        
        <div className="space-y-3">
          {SUBJECTS.map((subject, index) => {
            const progress = getSubjectProgress(subject.id);
            const percent = Math.round((progress.correct / progress.total) * 100);
            
            return (
              <Card 
                key={subject.id} 
                className="border-border animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-lg">
                      {subject.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{subject.name}</h4>
                      <p className="text-xs text-muted-foreground">{subject.fullName}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-foreground">{percent}%</span>
                    </div>
                  </div>
                  
                  <Progress value={percent} className="h-2 bg-muted mb-2" />
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{progress.studied} gelernt</span>
                    <span className="text-success">{progress.correct} richtig</span>
                    {progress.incorrect > 0 && (
                      <span className="text-destructive">{progress.incorrect} falsch</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Member Since */}
      <div className="px-4 mt-6">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Mitglied seit</p>
                <p className="text-foreground font-medium">
                  {user?.createdAt 
                    ? new Date(user.createdAt).toLocaleDateString('de-DE', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'Heute'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logout Button */}
      <div className="px-4 mt-8">
        <Button 
          variant="outline" 
          className="w-full border-destructive/30 text-destructive hover:bg-destructive/10"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Abmelden
        </Button>
      </div>

      {/* Legal Links */}
      <div className="px-4 mt-6">
        <Card 
          className="border-border cursor-pointer hover:bg-secondary/30 transition-colors"
          onClick={() => setShowLegal(true)}
          data-testid="legal-link"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Rechtliches</p>
                  <p className="text-xs text-muted-foreground">Impressum, Datenschutz, AGB</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* App Info */}
      <div className="px-4 mt-6 text-center">
        <p className="text-xs text-muted-foreground">
          BQ Learn App v1.0.0
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Daten werden lokal gespeichert
        </p>
      </div>
    </div>
  );
};

export default ProfileScreen;